import { events } from '@nykaa/nes-data-layer/constants';
import { pushEvent } from '@nykaa/nes-data-layer/utils';
import { variantTypes } from 'shared/constants';
import { TagObject } from '@nykaa/nes-product-card/types';
import { isValidObject } from '@nykaa/nes-utils/validations';
import { localStorageService } from 'service/storage';
import { logger } from '@nykaa/nes-logger';
import {
  RECENTTY_VIEWED_PRODUCTS,
  RECENTLY_VIEWED_SORT_ORDER,
  RecentlyViewedSortOrder,
  RECENTLY_VIEWED_EXPIRY_DAYS,
} from 'constants/productPage';
import {
  PrimaryCategories,
  RatingsVisibility,
  Variant,
} from 'store/product/types';
import { useLocation } from 'react-router-dom';
import { getPageType } from 'components/Header/helper';
import PageTypes from 'constants/pageTypes';

export const isConfigurable = (type: string) =>
  type &&
  type.toUpperCase() !== variantTypes.SIMPLE &&
  type.toUpperCase() !== variantTypes.BUNDLE;

export const pushVideoPlay = (
  productId: string,
  positionInCarousel: number
) => {
  pushEvent(events.PRODUCT_VIDEO_PLAY, {
    productVideoPlay: `${productId}:${positionInCarousel}`,
  });
};

export const getTagObjects = (tags: string[], messages = {}): TagObject[] =>
  tags.map((tag) => {
    const tagStringID = `${tag.toUpperCase()}_TAG`;
    const translatedName = messages[tagStringID] || tag;
    return {
      id: tag,
      name: translatedName,
    };
  });

interface RecentlyViewedItem {
  id: string;
  timestamp: number;
}

const filterAndSetProductAsViewed = (
  cachedList: RecentlyViewedItem[],
  productID: string
): RecentlyViewedItem[] => {
  if (isNaN(+productID)) {
    return cachedList;
  }
  const productCount = cachedList.length;
  const modifiedViewedList = [...cachedList];
  if (!productCount) {
    modifiedViewedList?.push({ id: productID, timestamp: Date.now() });
    return modifiedViewedList;
  }
  // Check if productID is already available in recently Viewed List
  const productIndex = cachedList.findIndex(
    (product) => product.id === productID
  );
  if (productIndex >= 0) {
    modifiedViewedList?.splice(productIndex, 1);
  }
  modifiedViewedList.push({ id: productID, timestamp: Date.now() });
  if (modifiedViewedList.length > 20) {
    modifiedViewedList?.shift();
  }
  return modifiedViewedList;
};

/**
 * Internal helper to parse recently viewed data from localStorage
 * Handles both old format (string[]) and new format (RecentlyViewedItem[])
 * @returns Array of RecentlyViewedItem objects with id and timestamp
 */
const parseRecentlyViewedFromStorage = (): RecentlyViewedItem[] => {
  const recentyViewedItems = localStorageService.getItem(
    RECENTTY_VIEWED_PRODUCTS
  );

  if (!recentyViewedItems) {
    return [];
  }

  try {
    const data = JSON.parse(recentyViewedItems);

    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    // Check format of first item to determine old vs new format
    if (typeof data[0] === 'string') {
      // Old format: ["id1", "id2"] - convert to new format with current timestamp
      return data.map((id: string) => ({ id, timestamp: Date.now() }));
    } else if (typeof data[0] === 'object' && data[0].id) {
      // New format: [{id: "id1", timestamp: 123}, ...]
      return data;
    }

    return [];
  } catch (err) {
    logger.error(
      err,
      'Error while parsing recently viewed items from local storage'
    );
    return [];
  }
};

export const getRecenltyViewedProductsFromLocal = (): string[] => {
  const recentlyViewedList = parseRecentlyViewedFromStorage();
  return recentlyViewedList.map((item) => item.id);
};

export const setProductAsRecentlyViewed = (productID: string) => {
  const recentyViewedItems = localStorageService.getItem(
    RECENTTY_VIEWED_PRODUCTS
  );
  let cachedList: RecentlyViewedItem[] = [];

  if (recentyViewedItems) {
    try {
      const data = JSON.parse(recentyViewedItems);
      if (Array.isArray(data)) {
        if (data[0] && typeof data[0] === 'string') {
          // Old format: migrate to new format
          cachedList = data.map((id: string) => ({
            id,
            timestamp: Date.now(),
          }));
        } else if (data[0] && typeof data[0] === 'object' && data[0].id) {
          // New format
          cachedList = data;
        }
      }
    } catch (err) {
      logger.error(
        err,
        'Error while parsing recently view items from local storage'
      );
    }
  }

  localStorageService.setItem(
    RECENTTY_VIEWED_PRODUCTS,
    JSON.stringify(filterAndSetProductAsViewed(cachedList, productID))
  );
};

export const getValidRecentlyViewedProductIds = (
  expiryDays: number = RECENTLY_VIEWED_EXPIRY_DAYS,
  sortOrder: RecentlyViewedSortOrder = RECENTLY_VIEWED_SORT_ORDER.LATEST_FIRST,
  excludeId?: string
): string[] => {
  const recentlyViewedList = parseRecentlyViewedFromStorage();

  if (recentlyViewedList.length === 0) {
    return [];
  }

  // Filter by expiry
  const expiryTimestamp = Date.now() - expiryDays * 24 * 60 * 60 * 1000;
  let filteredList = recentlyViewedList.filter(
    (item) => item.timestamp >= expiryTimestamp
  );

  // Exclude specific product ID if provided
  if (excludeId) {
    filteredList = filteredList.filter((item) => item.id !== excludeId);
  }

  // Sort based on sortOrder
  filteredList.sort((a, b) => {
    if (sortOrder === RECENTLY_VIEWED_SORT_ORDER.LATEST_FIRST) {
      return b.timestamp - a.timestamp; // Latest first
    } else {
      return a.timestamp - b.timestamp; // Oldest first
    }
  });

  return filteredList.map((item) => item.id);
};

export const checkCategoryEligibility = (
  categories: string[],
  primaryCategories: PrimaryCategories
) => {
  if (!primaryCategories) {
    return false;
  }

  const { l1, l2, l3 } = primaryCategories;

  const primaryCategoryIds = [l1?.id, l2?.id, l3?.id];
  return primaryCategoryIds.some((id) => categories.includes(id));
};

export const checkRatingsVisibility = (
  rating: number,
  ratingCount: number,
  ratingsVisibility: RatingsVisibility
) => {
  const { ratingsCountLimit = 0, avgRatingValueLimit = 0 } =
    ratingsVisibility || {};
  if (!(ratingCount < ratingsCountLimit && rating < avgRatingValueLimit)) {
    return true;
  }
  return false;
};

export const isValidRecoGroup = (group: string, groups: any) => {
  if (isValidObject(groups)) {
    return groups?.[group]?.includes('cav_brand_category') || false;
  }
  return false;
};

export const getPRData = (
  isDifferential: boolean,
  isPRV5Enabled: boolean,
  currentSku: Variant
) => {
  const { prData, cohortSaleTemplate, cohortSaleTemplateV2 } = currentSku || {};
  if (isDifferential) {
    if (isPRV5Enabled) {
      return cohortSaleTemplateV2;
    } else return cohortSaleTemplate;
  } else return prData;
};

export const checkIfIsMiniEnabled = (
  productCategories: string[],
  isMiniProductEnabled: boolean,
  primaryCategories: PrimaryCategories
): boolean => {
  if (!isMiniProductEnabled || !primaryCategories) return false;
  return (
    productCategories?.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};

export const checkIfFullHeightImgContainer = (
  productCategories: string[],
  isFullHeightContainer: boolean,
  primaryCategories: PrimaryCategories
): boolean => {
  if (!isFullHeightContainer || !primaryCategories || !productCategories) {
    return false;
  }
  const { l1, l2, l3 } = primaryCategories;
  const primaryCategoryIds = [l1?.id, l2?.id, l3?.id];
  return primaryCategoryIds.some((id) => productCategories.includes(id));
};

export const getCarouselStyle = (
  newSimilarEnabled: boolean,
  isFullHeightImg: boolean
): { height: string } => {
  if (isFullHeightImg) {
    return { height: '520px' };
  }
  if (newSimilarEnabled) {
    return { height: 'auto' };
  }
  return { height: '390px' };
};

export const useIsPDPage = () => {
  const { pathname } = useLocation();
  const pageType = getPageType(pathname);
  return pageType === PageTypes.PRODUCT;
};
export const useIsHLPPage = () => {
  const { pathname } = useLocation();
  const pageType = getPageType(pathname);
  return pageType === PageTypes.HLP;
};

export const getImageDimensions = (
  hasDynamicCard: boolean,
  cardWidth: number,
  cardHeight: number,
  imageResizeExp?: boolean
) => {
  const DEFAULT_IMAGE_SIZE = 200;
  const FULL_HEIGHT_DIMENSIONS = { width: 300, height: 400 };
  if (hasDynamicCard) {
    return { width: cardWidth, height: cardHeight };
  }
  return imageResizeExp
    ? FULL_HEIGHT_DIMENSIONS
    : { width: DEFAULT_IMAGE_SIZE, height: DEFAULT_IMAGE_SIZE };
};

export const isImageResizeEligible = (
  productCategories: string[],
  isImageResizeExpEnabled?: boolean,
  primaryCategories?: PrimaryCategories
): boolean => {
  if (!isImageResizeExpEnabled || !primaryCategories || !productCategories)
    return false;
  return (
    productCategories?.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};

export const isPlpVariantColorExpEligible = (
  productCategories: string[],
  plpVariantExp?: boolean,
  primaryCategories?: PrimaryCategories
): boolean => {
  if (!plpVariantExp || !primaryCategories || !productCategories) return false;
  return (
    productCategories?.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};

export const isPlpImageCarouselEligible = (
  productCategories: string[],
  plpImageCarouselExp?: boolean,
  primaryCategories?: PrimaryCategories
): boolean => {
  if (!plpImageCarouselExp || !primaryCategories || !productCategories)
    return false;
  return (
    productCategories.some((id) => id === primaryCategories?.l1?.id) ?? false
  );
};
